import ServiceLayout from "@/components/ServiceLayout";
import { AlertCircle } from "lucide-react";

export default function Veluro() {
  return (
    <ServiceLayout
      slug="veluro-ai-marketing"
      eyebrow="Solutions · Veluro — AI Marketing Platform"
      title="Meet your AI-powered growth team."
      intro="Veluro finds high-intent buyers, launches campaigns across every channel, books meetings, and watches the health of your business around the clock. Riverstone Partners introduces you to Veluro and stays engaged as the work begins. Learn more at veluro.ai."
      image="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1600&auto=format&fit=crop"
      imageAlt="Performance analytics graphs on a laptop screen"
      ctaLabel="Meet My AI Growth Team"
      providerLink={{ href: "https://veluro.ai", label: "Learn more about Veluro at veluro.ai" }}
      whoFor={[
        "Businesses that want marketing to keep working after hours.",
        "Teams without an in-house growth function.",
        "Owners tired of campaigns that stall between busy seasons.",
        "Companies that want AI finding buyers while people build relationships.",
      ]}
      problems={[
        "Leads that never get a timely follow-up.",
        "Campaigns that only run when someone finds the time.",
        "Channels managed one by one, with no single view.",
        "No always-on picture of marketing health.",
      ]}
      capabilities={[
        { title: "AI buyer discovery", body: "Finds high-intent buyers actively looking for what you offer." },
        { title: "Cross-channel campaigns", body: "Launches campaigns across every channel from one engine." },
        { title: "Meeting booking", body: "Turns interest into scheduled conversations on your calendar." },
        { title: "24/7 business health monitoring", body: "Watches the health of your business around the clock." },
        { title: "Email follow-up & lead nurturing", body: "Systematic follow-up so fewer opportunities slip away." },
        { title: "Reporting & optimization", body: "Clear reporting on what’s working, refined over time." },
      ]}
      providerRole={[
        {
          title: "Veluro’s role — platform & delivery",
          body: "Veluro is the AI-powered growth team behind this offering. Its platform finds high-intent buyers, launches and manages campaigns across every channel, books meetings, and monitors the health of your business 24/7. Visit veluro.ai to explore the platform itself.",
        },
        {
          title: "Riverstone’s role — connection & continuity",
          body: "Riverstone Partners introduces you to Veluro, helps frame your growth goals, and remains a point of contact as the engagement begins. The platform, campaigns, and results are delivered by Veluro.",
        },
      ]}
      conversation={[
        { title: "Your growth goals", body: "Who you want to reach, what a good meeting looks like, and where growth should come from." },
        { title: "Where AI fits", body: "How Veluro’s buyer discovery, campaigns, and booking would plug into the way you work today." },
        { title: "A working session", body: "A closer look at the platform with the Veluro team, and what getting started would involve." },
      ]}
      faqs={[
        { q: "Is Veluro part of Riverstone Partners?", a: "No. Veluro is an independent company and the provider behind this offering. Riverstone introduces you and stays engaged, but the platform and campaigns are Veluro’s. Learn more at veluro.ai." },
        { q: "Does AI replace our marketing team?", a: "Veluro is designed to act as your growth team — or to multiply a small one. It handles discovery, campaigns, and follow-up continuously; your people stay in charge of relationships." },
        { q: "What does it cost?", a: "Pricing is set by Veluro based on your needs and is confirmed directly with them. We don’t quote one-size-fits-all prices." },
        { q: "How is this different from Marketing & Website Design?", a: "They’re related: Veluro powers both. The Marketing & Website Design service focuses on your websites, funnels, and content; this one focuses on the AI platform that finds buyers, runs campaigns, and books meetings. The final package is confirmed during your conversation." },
        { q: "Do you guarantee results?", a: "No. We don’t promise specific campaign outcomes, lead counts, or revenue — and we’re cautious of anyone who does." },
      ]}
      extra={
        <p className="mt-14 flex items-start gap-3 border border-charcoal/10 bg-sage/40 p-6 text-sm leading-relaxed text-charcoal/70">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-forest" />
          Capabilities above summarize Veluro’s published description of its platform
          (veluro.ai). The final package for your engagement—exactly what’s included—is
          confirmed with Veluro during your initial conversation.
        </p>
      }
    />
  );
}
